import { useEffect, forwardRef, useImperativeHandle, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { Container, TextInput, Select, Text } from '@mantine/core';
import { useLocation } from 'react-router-dom';
import { useForm } from "@mantine/form";
import { useDebouncedValue } from "@mantine/hooks";
import DateInput from '../components/DateInputES';
import { Row, Col } from 'react-bootstrap';
import { SaveResponses } from "../actions/ResponsesActions";
import { GetTipoDocumento, GetProfesiones, GetEstadoCivil, GetPaises } from "../actions/FormDataActions";
import GenderEnum from '../enums/GenderEnum';
import ModalKnownUser from "../components/ModalKnownUser";
import { stepIds } from "../enums/fieldsDataConfig";
import { useStepResponses } from "../hooks/useStepResponses";
import useFormTracking from "../hooks/useFormTracking";

const PersonalDataForm = forwardRef(({ stepNumber,queryParams, id }, ref) => {
  const dispatch = useDispatch();
  
  const response = useSelector((state) => state.respuestas.steps[stepNumber]?.responses || {});
  const {data: dataDocumentos} = useSelector((state) => state.datosFormulario.tiposDoc);
  const {data: dataProfesiones} = useSelector((state) => state.datosFormulario.profesiones);
  const {data: dataEstadosCiviles} = useSelector((state) => state.datosFormulario.estadosCiviles);
  const {data: dataPaises} = useSelector((state) => state.datosFormulario.paises);
  const ProgramResponse = useStepResponses(stepIds.PROGRAMA);


  const [knownUserOpened, setKnownUserOpened] = useState(false);
  const [knownUserMails, setKnownUserMails] = useState([]);
  const [knownUserMailSending, setKnownUserMailSending] = useState(false)
  const [knownUserMailSended, setKnownUserMailSended] = useState(false)
  const [selectKey, setSelectKey] = useState(0);
  const { track } = useFormTracking(stepNumber);

  // Helper: setea el campo en el form y emite field_completed (solo Selects no-PII)
  const trackSelect = (fieldName) => (value) => {
    form.setFieldValue(fieldName, value);
    if (value) track('field_completed', { fieldName });
  };

  // Formulario con validación
  const form = useForm({
    initialValues: {
      //persona
      nombres: response.nombres || '',
      apellidos: response.apellidos || '',
      documentoNumero: response.documentoNumero || '',
      documentoTipo: response.documentoTipo || 'DNI',
      sexo: response.sexo || null,
      nacionalidad: response.nacionalidad || 'AR',
      fechaNacimiento: response.fechaNacimiento ? new Date(response.fechaNacimiento) : null,
      estadoCivil: response.estadoCivil || null,
      paisNacimiento: response.paisNacimiento || 'AR',
      profesion: response.profesion || null,
      //tutor
      tipoTutor: response.tipoTutor || null,
      paisResidenciaTutor: response.paisResidenciaTutor || 'AR',
      nombresTutor: response.nombresTutor || '',
      apellidosTutor: response.apellidosTutor || '',
      documentoTipoTutor: response.documentoTipoTutor || 'DNI',
      documentoNumeroTutor: response.documentoNumeroTutor || '',
      fechaNacimientoTutor: response.fechaNacimientoTutor ? new Date(response.fechaNacimientoTutor) : null,
    },
    validate: {
      //persona
      nombres: (value) => (value?.length > 2 ? null : "El campo es obligatorio"),
      apellidos: (value) => (value?.length > 2 ? null : "El campo es obligatoria"),
      documentoNumero: (value, values) => {
        if (values.documentoTipo === 'PAS') {
          // Validación para Pasaporte Extranjero: permite letras y números, máximo 12 caracteres
          return /^[A-Za-z0-9]{1,12}$/.test(value) ? null : "El campo debe tener entre 1 y 12 caracteres";
        }
        // Para otros tipos de documento, mantiene la validación original
        return /^\d{8}$/.test(value) ? null : "El campo debe tener 8 dígitos";
      },
      documentoTipo: (value) => (value ? null : "El campo es obligatorio"),
      sexo: (value) => (value ? null : "El campo es obligatorio"),
      nacionalidad: (value) => (value ? null : "El campo es obligatorio"),
      fechaNacimiento: (value) => {
        if (!value) return "El campo es obligatorio";
      
        const today = new Date();
        const birthDate = new Date(value);
        const age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        const dayDiff = today.getDate() - birthDate.getDate();
      
        const isBeforeBirthday = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0);
        const finalAge = isBeforeBirthday ? age - 1 : age;
      
       // if (finalAge < 18) return "Debe tener al menos 18 años";
        if (finalAge > 100) return "La edad no puede ser mayor a 100 años";
      
        return null;
      },
      estadoCivil: (value) => (value ? null : "El campo es obligatorio"),
      paisNacimiento: (value) => (value ? null : "El campo es obligatorio"),
      profesion: (value) => (value ? null : "El campo es obligatorio"),
      //tutor
      tipoTutor: (value, values) => (isMinor(values.fechaNacimiento) && queryParams.t === 'g' && !value ? "El campo es obligatorio" : null),
      paisResidenciaTutor: (value, values) => (isMinor(values.fechaNacimiento) && queryParams.t === 'g' && !value ? "El campo es obligatorio" : null),
      nombresTutor: (value, values) => (isMinor(values.fechaNacimiento) && queryParams.t === 'g' && value?.length <= 2 ? "El campo es obligatorio" : null),
      apellidosTutor: (value, values) => (isMinor(values.fechaNacimiento) && queryParams.t === 'g' && value?.length <= 3 ? "El campo es obligatorio" : null),
      documentoTipoTutor: (value, values) => (isMinor(values.fechaNacimiento) && queryParams.t === 'g' && !value ? "El campo es obligatorio" : null),
      documentoNumeroTutor: (value, values) => (isMinor(values.fechaNacimiento) && queryParams.t === 'g' && !/^\d{8}$/.test(value) ? "El campo es obligatorio" : null),
      fechaNacimientoTutor: (value, values) => (isMinor(values.fechaNacimiento) && queryParams.t === 'g' && !value ? "El campo es obligatorio" : null),
    },
  });

  const [debouncedValues] = useDebouncedValue(form.values, 300);

  useEffect(() => {
    dispatch(SaveResponses(id, debouncedValues));
  }, [debouncedValues, dispatch, id]);

  useEffect(() => {
    dispatch(GetTipoDocumento());
    dispatch(GetProfesiones());
    dispatch(GetEstadoCivil());
    dispatch(GetPaises());
  }, []);

  // Verificar si ya se envió un mail y abrir el modal automáticamente
  useEffect(() => {
    const mailSent = sessionStorage.getItem('mailSentSuccessfully');
    if (mailSent === 'true' && !knownUserOpened) {
      setKnownUserOpened(true);
    }
  }, [knownUserOpened]);

  useEffect(() => {
    if (knownUserOpened) track('field_completed', { fieldName: 'known_user_modal_opened' });
  }, [knownUserOpened]);

  // Limpiar sessionStorage si hay un token válido (usuario ya validado)
  useEffect(() => {
    if (queryParams.token && queryParams.token !== 'undefined') {
      sessionStorage.removeItem('mailSentSuccessfully');
    }
  }, [queryParams.token]);


  const validateUserId = async (tipoDocumento, numeroDocumento) => {

    if (!tipoDocumento || !numeroDocumento || !/^\d{8}$/.test(numeroDocumento))
      return false;
  
    if(queryParams.token && queryParams.token !== 'undefined')
      return false;
    
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/checkout/user`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
       body: JSON.stringify({ tipoDocumento, numeroDocumento, carrera:ProgramResponse.carrera,tipocarrera:queryParams.t}),
      });
  
      if (!response.ok)
        return false;
  
      const data = await response.json();

      const emailsSearchs = data.emails;
      
      if (!emailsSearchs || !data.emails) {
        return false;
      }
      
      setKnownUserMails(emailsSearchs);
      return true;

    } catch (error) {
      return false;
    }
  };

  
  const sendAutocompleteMail = async (mails) => {
    try {

      const url = `${process.env.REACT_APP_API_BASE_URL}/checkout/user/auto-complete`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          documentoTipo: form.values.documentoTipo,
          documentoNumero: form.values.documentoNumero, 
          mails: mails, 
          fecha:ProgramResponse.fechaIngreso,
          carrera:ProgramResponse.carrera,
          campus:ProgramResponse.campus,
          anioCursada:ProgramResponse.anioCursada,
          turno:ProgramResponse.turno,
          t:queryParams.t,
          c:queryParams.c,
          p:queryParams.p,
          fi:queryParams.fi,
          em:queryParams.em,
        }),
      });
  
      if (!response.ok) {
        if (response.status === 404) {
          console.error("Hubo un error al enviar el mail.");
        }
        return false;
      }
      
      return true;

    } catch (error) {
      return false;
    }
  };

  const handleSaveResponseId = async (idNum) => {
    await form.setFieldValue('documentoNumero', idNum);
  };

  const handleSendMail = async (data) => {
    setKnownUserMailSending(true);
    const result = await sendAutocompleteMail(data);
    setKnownUserMailSending(false);
    if (result) {
      setKnownUserMailSended(true);
      track('field_completed', { fieldName: 'known_user_mail_sent' });
    } else {
      track('field_error', { fieldName: 'known_user_mail_sent' });
    }
    return result;
  };

  useImperativeHandle(ref, () => ({
    validate: () => {
      const validation = form.validate();
      if (validation.hasErrors) {
        Object.keys(validation.errors).forEach((field) =>
          track('field_error', { fieldName: field })
        );
        return false;
      }
      return true;
    },
  }));

  const isMinor = (birthDate) => {
    if (!birthDate) return false;
    const today = new Date();
    const birthDateObj = new Date(birthDate);
    let age = today.getFullYear() - birthDateObj.getFullYear();
    const monthDifference = today.getMonth() - birthDateObj.getMonth();
    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDateObj.getDate())) {
      age--;
    }
    return age < 18;
  };

  return (
    <Container size="100%">
      <Row classlabel="row-inputs">
        <Col md={6}>
          <TextInput
            required
            label="Nombres"
            placeholder="Juan"
            {...form.getInputProps("nombres")}
            onPaste={(e) => e.preventDefault()} 
            onKeyDown={(event) => {
              if (!/^[a-zA-Z\sáéíóúÁÉÍÓÚñÑ]*$/.test(event.key) && event.key !== "Backspace" && event.key !== "Tab") {
                event.preventDefault(); 
              }
            }}
            disabled={!!queryParams.token}
          />
        </Col>
        <Col md={6}>
          <TextInput
            required
            label="Apellidos"
            placeholder="Gomez"
            {...form.getInputProps("apellidos")}
            onPaste={(e) => e.preventDefault()} 
            onKeyDown={(event) => {
              if (!/^[a-zA-Z\sáéíóúÁÉÍÓÚñÑ]*$/.test(event.key) && event.key !== "Backspace" && event.key !== "Tab") {
                event.preventDefault();
              }
            }}
            disabled={!!queryParams.token}
          />
        </Col>
      </Row>
      <Row classlabel="row-inputs">
        <Col md={6}>
          <Row>
            <Col md={6} lg={5}>
              <Select
                key={selectKey}
                required
                label="Tipo Documento"
                data={dataDocumentos}
                flex={1}
                checkIconPosition='right'
                placeholder='Seleccione una opción'
                {...form.getInputProps("documentoTipo")}
                onChange={trackSelect('documentoTipo')}
                disabled={!!queryParams.token}
              />
            </Col>
            <Col md={6} lg={7}>
            <TextInput
                id="documentoNumero"
                type={form.values.documentoTipo === 'PAS' ? 'text' : 'number'}
                required
                flex={2}
                onKeyDown={(event) => {
                  if (form.values.documentoTipo === 'PAS') {
                    // Solo permite letras y números para PAS
                    if (!/^[a-zA-Z0-9]$/.test(event.key) && event.key !== "Backspace" && event.key !== "Tab") {
                      event.preventDefault();
                    }
                  } else {
                    // Si no es PAS, solo permite números
                    if (!/^\d$/.test(event.key) && event.key !== "Backspace" && event.key !== "Tab") {
                      event.preventDefault();
                    }
                  }
                }}
                onPaste={e => {
                  e.preventDefault();
                  const pasted = e.clipboardData.getData('Text').replace(/[^a-zA-Z0-9]/g, '');
                  form.setFieldValue('documentoNumero', pasted);
                }}
                onBlur={async (e) => {
                    if (form.values.documentoTipo === 'DNI') {
                      const exists = await validateUserId(form.values.documentoTipo, form.values.documentoNumero);
                      if (exists) {
                        setSelectKey(k => k + 1);
                        document.body.click();
                        setKnownUserOpened(true);
                      }
                    }
                  
                }}
                placeholder="Ej: 11 111 111"
                label="Numero Documento"
                value={form.values.documentoNumero}
                onChange={async (event) => {
                  const newValue = event.target.value;
                  form.setFieldValue("documentoNumero", newValue);
                }}
                error={form.errors.documentoNumero}
                disabled={!!queryParams.token}
              />
            </Col>
          </Row>
        </Col>
        <Col md={6}>
          <Select
            key={selectKey}
            required
            label="Sexo"
            data={GenderEnum}
            checkIconPosition="right"
            placeholder="Seleccione una opción"
            {...form.getInputProps("sexo")}
            onChange={trackSelect('sexo')}
          />
        </Col>
      </Row>
      <Row classlabel="row-inputs">
        <Col md={4}>
          <Select
            required
            searchable
            label="Nacionalidad"
            data={dataPaises}
            checkIconPosition="right"
            placeholder="Argentina"
            {...form.getInputProps("nacionalidad")}
            onChange={trackSelect('nacionalidad')}
            disabled={!!queryParams.token}
          />
        </Col>
        <Col md={4}>
          <DateInput
            label="Fecha de Nacimiento"
            required
            maxDate={new Date()}
            placeholder="(dd/mm/aaaa)"
            defaultDate={new Date(2000,0,1)}
            {...form.getInputProps("fechaNacimiento")}
            disabled={!!queryParams.token}
          />
        </Col>
        <Col md={4}>
          <Select
            required
            label="Situacion Civil"
            searchable
            data={
              dataEstadosCiviles?.map((estado) => {
                const [key, value] = Object.entries(estado)[0];
                return { value, label: estado.vc_descripcion };
              })
            }
            checkIconPosition="right"
            placeholder="Seleccione una opción"
            {...form.getInputProps("estadoCivil")}
            onChange={trackSelect('estadoCivil')}
          />
        </Col>
      </Row>
      <Row classlabel="row-inputs" style={{ marginTop: "20px" }}>
        <Col md={6}>
          <Select
            required
            label="País de Nacimiento"
            searchable
            data={dataPaises}
            checkIconPosition="right"
            placeholder="Seleccione una opción"
            {...form.getInputProps("paisNacimiento")}
            onChange={trackSelect('paisNacimiento')}
            disabled={!!queryParams.token}
          />
        </Col>
        <Col md={6}>
          <Select
            required
            searchable
            label="Profesión"
            placeholder="Abogado/a"
            flex={1}
            data={
              dataProfesiones?.map((profesion) => {
                const [key, value] = Object.entries(profesion)[0];
                return { value, label: profesion.Descripción };
              })
            }
            checkIconPosition="right"
            {...form.getInputProps("profesion")}
            onChange={trackSelect('profesion')}
          />
        </Col>
      </Row>
      {isMinor(form.values.fechaNacimiento) && queryParams.t === 'g' && (
        <>
          <Text size="xl" fw={500} mb={'xs'} mt={'xl'} c={'rgb(85, 85, 85)'}>Datos del Padre, Madre o Tutor</Text>
          <Row>
            <Col md={6}>
              <Select
                label="Tipo"
                placeholder="Seleccione una opción"
                required
                data={[{ value: 'padre', label: 'Padre' }, { value: 'madre', label: 'Madre' }, { value: 'tutor', label: 'Tutor' }]}
                {...form.getInputProps('tipoTutor')}
                onChange={trackSelect('tipoTutor')}
              />
            </Col>
            <Col md={6}>
              <Select
                required
                label="País de residencia"
                placeholder="Seleccione una opción"
                searchable
                data={dataPaises}
                {...form.getInputProps('paisResidenciaTutor')}
                onChange={trackSelect('paisResidenciaTutor')}
              />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <TextInput
                required
                label="Nombres"
                placeholder="Ingrese los nombres"
                {...form.getInputProps('nombresTutor')}
                onKeyDown={(event) => {
                  if (!/^[a-zA-Z\sáéíóúÁÉÍÓÚñÑ]*$/.test(event.key) && event.key !== "Backspace" && event.key !== "Tab") {
                    event.preventDefault();
                  }
                }}
              />
            </Col>
            <Col md={6}>
              <TextInput
                required
                label="Apellidos"
                placeholder="Ingrese los apellidos"
                {...form.getInputProps('apellidosTutor')}
                onKeyDown={(event) => {
                  if (!/^[a-zA-Z\sáéíóúÁÉÍÓÚñÑ]*$/.test(event.key) && event.key !== "Backspace" && event.key !== "Tab") {
                    event.preventDefault();
                  }
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Row>
                <Col md={6} lg={5}>
                  <Select
                    key={selectKey}
                    required
                    label="Tipo de Documento"
                    placeholder="Seleccione una opción"
                    data={dataDocumentos}
                    {...form.getInputProps('documentoTipoTutor')}
                    onChange={trackSelect('documentoTipoTutor')}
                  />
                </Col>
                <Col md={6} lg={7}>
                  <TextInput
                    required
                    type="number"
                    label="Número de Documento"
                    placeholder="Ingrese el número de documento"
                    {...form.getInputProps('documentoNumeroTutor')}
                  />
                </Col>
              </Row>
            </Col>
            <Col md={6}>
              <DateInput
                label="Fecha de Nacimiento"
                required
                maxDate={new Date()}
                placeholder="(dd/mm/aaaa)"
                {...form.getInputProps('fechaNacimientoTutor')}
              />
            </Col>
          </Row>
        </>
      )}
      <ModalKnownUser
        opened={knownUserOpened}
        onClose={null}
        knownMails={knownUserMails}
        accept={handleSendMail}
        sendingMail={knownUserMailSending}
        sendedMail={knownUserMailSended}
      />
      
    </Container>
  );
});

export default PersonalDataForm;